import { Box, CircularProgress } from '@mui/material'
import { L, Icon } from '../ui/LavDropdown'
import { LavSectionTitle } from '../add-appointment/appointmentLavenderFields'
import UpcomingCard from './UpcomingCard'
import OverlayScrollThumb from '../ui/OverlayScrollThumb'
import MsgIcon from './MsgIcon'
import { msgInitials } from './messagingUtils'

/** "View profile ›" link line (design `.msg-profile-link`). */
export function ViewProfileLink({ fontSize = '11.5px' }) {
	return (
		<Box
			className="msg-profile-link"
			sx={{
				display: 'inline-flex', alignItems: 'center', gap: '4px', mt: '3px',
				fontSize, fontWeight: 750, color: L.primaryDeep,
				'& svg': { transition: 'transform 0.15s ease' },
			}}
		>
			View profile
			<MsgIcon name="chev" size={11} sw={2.2} />
		</Box>
	)
}

/** Initials avatar shared by the profile views. */
export function MsgAvatar({ name, size = 36, fontSize = 13, white = false }) {
	return (
		<Box sx={{
			width: size, height: size, flex: 'none', borderRadius: '50%',
			bgcolor: white ? '#FFFFFF' : L.primarySoft, color: L.primaryDeep,
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			fontSize: `${fontSize}px`, fontWeight: 800, letterSpacing: '0.02em',
		}}>
			{msgInitials(name)}
		</Box>
	)
}

/**
 * Pets + Upcoming sections — shared between the desktop profile sidebar and
 * the mobile client-details screen.
 */
export function ProfileSections({ pets, upcoming, loading, tz, formatCurrency }) {
	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
				<CircularProgress size={22} sx={{ color: L.primary }} />
			</Box>
		)
	}
	return (
		<>
			<Box component="section">
				<LavSectionTitle>Pets</LavSectionTitle>
				{pets.length === 0 ? (
					<EmptyNote>No pets on file</EmptyNote>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{pets.map(p => (
							<Box key={p.id} sx={{
								display: 'flex', alignItems: 'center', gap: '10px',
								border: `1px solid ${L.border}`, borderRadius: L.rSm,
								bgcolor: L.field, p: '8px 10px',
							}}>
								<Box sx={{
									width: 32, height: 32, flex: 'none', borderRadius: '50%',
									bgcolor: L.primarySoft, color: L.primaryDeep,
									display: 'flex', alignItems: 'center', justifyContent: 'center',
								}}>
									<Icon name="paw" size={15} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Box component="strong" sx={{ display: 'block', fontSize: '14px', fontWeight: 750, color: L.text }}>
										{p.name}
									</Box>
									<Box component="span" sx={{
										display: 'block', fontSize: '12px', color: L.muted, mt: '1px',
										whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
									}}>
										{[p.breed, p.species].filter(Boolean).join(' | ') || '—'}
									</Box>
								</Box>
							</Box>
						))}
					</Box>
				)}
			</Box>
			<Box component="section">
				<LavSectionTitle>Upcoming</LavSectionTitle>
				{upcoming.length === 0 ? (
					<EmptyNote>No upcoming appointments</EmptyNote>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{upcoming.map(a => (
							<UpcomingCard key={a.id} appt={a} tz={tz} formatCurrency={formatCurrency} />
						))}
					</Box>
				)}
			</Box>
		</>
	)
}

function EmptyNote({ children }) {
	return (
		<Box sx={{
			bgcolor: L.surface, border: `1px solid ${L.border}`, borderRadius: L.r,
			p: '12px', textAlign: 'center',
		}}>
			<Box component="span" sx={{ fontSize: '12.5px', color: L.muted, fontWeight: 600 }}>
				{children}
			</Box>
		</Box>
	)
}

/** Desktop right-hand client profile sidebar. */
export function DesktopProfileSidebar({ clientName, phone, pets, upcoming, loading, tz, formatCurrency, onViewProfile = null }) {
	return (
		<Box component="aside" className="lav-overlay-scroll" sx={{
			width: 290, flex: 'none', borderLeft: `1px solid ${L.border}`, bgcolor: L.bg,
			p: '22px 18px', display: 'flex', flexDirection: 'column', gap: '20px',
			overflowY: 'auto', minHeight: 0,
		}}>
			<OverlayScrollThumb />
			{/* Identity head — when navigation is wired up, the whole block is a
			    button that jumps to the client's profile (design `.msg-profile-head`). */}
			<Box
				component={onViewProfile ? 'button' : 'div'}
				type={onViewProfile ? 'button' : undefined}
				onClick={onViewProfile || undefined}
				aria-label={onViewProfile ? `Open ${clientName}’s profile` : undefined}
				sx={{
					display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', textAlign: 'center',
					...(onViewProfile ? {
						border: 'none', bgcolor: 'transparent', color: 'inherit', fontFamily: 'inherit',
						borderRadius: L.r, m: '-10px -8px -6px', p: '12px 8px 10px', cursor: 'pointer',
						transition: 'background 0.15s ease',
						'&:hover': { bgcolor: L.surface },
						'&:hover .msg-profile-link': { textDecoration: 'underline', textUnderlineOffset: '3px' },
						'&:hover .msg-profile-link svg': { transform: 'translateX(2px)' },
					} : {}),
				}}
			>
				<MsgAvatar name={clientName} size={52} fontSize={17} />
				<Box component="strong" sx={{ fontSize: '16px', fontWeight: 800, color: L.text }}>
					{clientName}
				</Box>
				{phone && (
					<Box component="span" sx={{ fontSize: '12.5px', color: L.muted, fontWeight: 650 }}>
						{phone}
					</Box>
				)}
				{onViewProfile && <ViewProfileLink />}
			</Box>
			<ProfileSections pets={pets} upcoming={upcoming} loading={loading} tz={tz} formatCurrency={formatCurrency} />
		</Box>
	)
}
